import { useCallback, useEffect, useRef, useState } from 'react';
import type { AngleKey, EditList } from '../../utils/editing/editList';
import { angleAt, focusAt } from '../../utils/editing/editList';
import { FaceSearchCancelled, type FrameAt, faceSampleTimes, findFaces, loadFaceDetector, trackFromFaces } from '../../utils/media/faceTrack';
import { angleFileMs } from './StageCanvas';
import { isReadableAssetUrl } from './usePreviewAudio';

export interface FollowAngle {
  angleKey: string;
  videoUrl: string;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
  durationMs: number;
}

export interface FollowPerson {
  /** While looking: how many frames of how many. */
  progress: { done: number; total: number } | null;
  /** What happened last time, for the panel. */
  message: { tone: 'ok' | 'info' | 'error'; text: string } | null;
  start: () => void;
  cancel: () => void;
  /** Back to one fixed position (where the crop is at `atMs`). */
  stop: (atMs: number) => void;
}

/**
 * "Follow the person": finds faces through the kept stretches and saves the crop's path
 * as one undo step. Runs on in the background if the creator switches tools.
 */
export function useFollowPerson(options: {
  list: EditList | null;
  videoUrl: string | null;
  angles?: FollowAngle[];
  masterKey?: string;
  /** Apply a change to the newest list (the creator may keep editing while it runs). */
  changeWith: (update: (current: EditList) => EditList, key?: string) => void;
}): FollowPerson {
  const { list, videoUrl, angles, masterKey = 'A', changeWith } = options;
  const [progress, setProgress] = useState<FollowPerson['progress']>(null);
  const [message, setMessage] = useState<FollowPerson['message']>(null);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const start = useCallback(() => {
    if (!list || !videoUrl || abortRef.current) return;
    const frameAt: FrameAt = angles && angles.length > 1
      ? (atMs) => {
          const wanted = angleAt(list.multicamCut, atMs, masterKey as AngleKey);
          const shown = angles.find((angle) => angle.angleKey === wanted && angleFileMs(angle, atMs) !== null)
            || angles.find((angle) => angleFileMs(angle, atMs) !== null);
          if (!shown) return null;
          return { url: shown.videoUrl, fileMs: angleFileMs(shown, atMs) as number };
        }
      : (atMs) => ({ url: videoUrl, fileMs: atMs });
    const urls = angles && angles.length > 1 ? angles.map((angle) => angle.videoUrl) : [videoUrl];
    if (!urls.every(isReadableAssetUrl)) {
      setMessage({ tone: 'error', text: 'This video can’t be read in the browser, so the person can’t be found. Position the crop by hand instead.' });
      return;
    }
    const times = faceSampleTimes(list);
    const controller = new AbortController();
    abortRef.current = controller;
    setMessage(null);
    setProgress({ done: 0, total: times.length });
    (async () => {
      try {
        const detector = await loadFaceDetector();
        const samples = await findFaces({ times, frameAt, detector, signal: controller.signal, onProgress: (done, total) => setProgress({ done, total }) });
        const track = trackFromFaces(samples);
        const seen = samples.filter((sample) => sample.faces.length > 0).length;
        if (track.length === 0) {
          setMessage({ tone: 'info', text: 'No face was found in the parts you kept, so the crop stays where you put it.' });
          return;
        }
        changeWith((current) => ({
          ...current,
          reframe: { ...(current.reframe || { focusXPct: 50, focusYPct: 45 }), mode: 'crop', track, focusXPct: track[0].focusXPct, focusYPct: track[0].focusYPct },
        }));
        const share = Math.round((seen / Math.max(1, samples.length)) * 100);
        setMessage({ tone: 'ok', text: `The crop now follows the person (${track.length} ${track.length === 1 ? 'position' : 'positions'}; a face was in ${share}% of the frames looked at). Play it to check; drag the crop on the picture to change any moment.` });
      } catch (error) {
        if (error instanceof FaceSearchCancelled) {
          setMessage({ tone: 'info', text: 'Stopped. Nothing was changed.' });
        } else {
          setMessage({ tone: 'error', text: `Couldn’t look for faces: ${error instanceof Error ? error.message : 'something went wrong'}. Check your connection and try again.` });
        }
      } finally {
        abortRef.current = null;
        setProgress(null);
      }
    })();
  }, [angles, changeWith, list, masterKey, videoUrl]);

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  const stop = useCallback((atMs: number) => {
    changeWith((current) => {
      if (!current.reframe) return current;
      const here = focusAt(current.reframe, atMs);
      return { ...current, reframe: { ...current.reframe, ...here, track: [] } };
    });
    setMessage(null);
  }, [changeWith]);

  return { progress, message, start, cancel, stop };
}
